import React, { useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Braces, ChevronRight } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const VARIABLES = [
  { group: "Event", tokens: [
    "event.event_id",
    "event.event_type",
    "event.event_source",
    "event.customer_code",
    "event.event_datetime",
    "event.payload.amount",
    "event.payload.currency",
    "event.payload.country",
    "event.payload.merchant",
    "event.payload.product",
    "event.payload.channel",
  ]},
  { group: "Customer", tokens: [
    "customer.code",
    "customer.segment",
    "customer.country",
    "customer.risk_score",
    "customer.lifetime_value",
    "customer.card_activated",
    "customer.tenure_days",
  ]},
  { group: "Enrichment", tokens: [
    "enrich.risk_score",
    "enrich.branch_id",
    "enrich.last_transaction_at",
  ]},
];

const OPERATORS = ["==", "!=", ">", "<", ">=", "<=", "&&", "||", "!", "contains", "in"];

const FUNCTIONS = [
  "now()",
  "days_ago(30)",
  "hours_ago(2)",
  "lower(str)",
  "upper(str)",
  "abs(n)",
  "round(n)",
];

/**
 * value / onChange: controlled string
 * expressionType: "js" | "sql" | "python"
 */
export default function ExpressionBuilder({
  value = "",
  onChange,
  rows = 4,
  label,
  placeholder = "e.g. {{ event.event_source == 'web' && event.payload.amount > 500 }}",
  testId = "expression",
}) {
  const ref = useRef(null);

  const insert = (token) => {
    const el = ref.current;
    if (!el) return onChange((value || "") + token);
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    }, 0);
  };

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <Label className="label-caps">{label}</Label>
          <div className="flex items-center gap-1">
            <VarPopover label="Variables" tokens={VARIABLES} onPick={(t) => insert(`{{ ${t} }}`)} testId={`${testId}-vars`} />
            <TokenMenu label="Operators" items={OPERATORS} onPick={(t) => insert(` ${t} `)} testId={`${testId}-ops`} />
            <TokenMenu label="Functions" items={FUNCTIONS} onPick={(t) => insert(t)} testId={`${testId}-fns`} />
          </div>
        </div>
      )}
      <Textarea
        ref={ref}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="font-mono text-xs leading-relaxed resize-y"
      />
      <div className="flex items-center gap-2 text-[0.7rem] text-muted-foreground">
        <Braces className="w-3 h-3 text-primary" />
        <span>
          Wrap dynamic values in <code className="font-mono text-primary">{"{{ }}"}</code> · use dot-notation to access nested payload attributes
        </span>
      </div>
    </div>
  );
}

const VarPopover = ({ label, tokens, onPick, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid={testId}>
        <Braces className="w-3 h-3" /> {label}
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-72 p-0">
      <div className="max-h-80 overflow-auto">
        {tokens.map((g) => (
          <div key={g.group} className="border-b border-border last:border-0">
            <div className="label-caps px-3 py-2 bg-secondary/60">{g.group}</div>
            <div className="py-1">
              {g.tokens.map((t) => (
                <button
                  key={t}
                  onClick={() => onPick(t)}
                  className="w-full text-left px-3 py-1.5 font-mono text-xs hover:bg-secondary flex items-center justify-between group"
                >
                  <span>{t}</span>
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 text-primary" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

const TokenMenu = ({ label, items, onPick, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" className="h-7 text-xs" data-testid={testId}>
        {label}
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-56 p-2">
      <div className="grid grid-cols-3 gap-1">
        {items.map((op) => (
          <button
            key={op}
            onClick={() => onPick(op)}
            className={cn(
              "font-mono text-xs px-2 py-1.5 border border-border rounded-sm",
              "hover:border-primary hover:bg-primary/10 hover:text-primary transition-colors"
            )}
          >
            {op}
          </button>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);
